/**
 * Fixed-layout containers (Phase 2 contract).
 *
 * The bank is 4 tabs × 16 slots stored as one flat 64-slot array
 * (flat index = tab*16 + slot). The inventory is a flat 28-slot array.
 * Slot placement is part of state — never inferred from item category.
 */
import { EQUIP_SLOT_IDS, ITEMS } from '../../data';
import type { EquipSlotId, ItemId, ItemStack, PlayerEquipment } from '../state';

export type { ItemStack };

export const BANK_TABS = 4;
export const BANK_TAB_SLOTS = 16;
export const BANK_SIZE = BANK_TABS * BANK_TAB_SLOTS;
export const INVENTORY_SIZE = 28;

export type ContainerId = 'bank' | 'inventory';

/** Address of one slot in one container. Bank indices are flat 0..63. */
export type SlotRef = { container: ContainerId; index: number };

export type Containers = {
  bank: ReadonlyArray<ItemStack | null>;
  inventory: ReadonlyArray<ItemStack | null>;
};

export function emptyBank(): Array<ItemStack | null> {
  return Array<ItemStack | null>(BANK_SIZE).fill(null);
}

export function emptyInventory(): Array<ItemStack | null> {
  return Array<ItemStack | null>(INVENTORY_SIZE).fill(null);
}

export function emptyEquipment(): PlayerEquipment {
  return Object.fromEntries(EQUIP_SLOT_IDS.map((slot) => [slot, null])) as PlayerEquipment;
}

export function containerSize(container: ContainerId): number {
  return container === 'bank' ? BANK_SIZE : INVENTORY_SIZE;
}

export function isValidRef(ref: SlotRef): boolean {
  return Number.isInteger(ref.index) && ref.index >= 0 && ref.index < containerSize(ref.container);
}

export function sameRef(a: SlotRef, b: SlotRef): boolean {
  return a.container === b.container && a.index === b.index;
}

export function getSlot(c: Containers, ref: SlotRef): ItemStack | null {
  return c[ref.container][ref.index] ?? null;
}

export function withSlot(c: Containers, ref: SlotRef, value: ItemStack | null): Containers {
  const next = [...c[ref.container]];
  next[ref.index] = value;
  return { ...c, [ref.container]: next };
}

export function maxStackOf(itemId: ItemId): number {
  return ITEMS.items[itemId]?.maxStack ?? 1;
}

/** Total quantity of an item across both containers. */
export function countItem(c: Containers, itemId: ItemId): number {
  return [...c.bank, ...c.inventory].reduce(
    (sum, s) => sum + (s?.itemId === itemId ? s.qty : 0),
    0,
  );
}

/**
 * Best target slot for a quick move of `itemId` into `container`:
 * first same-item stack with headroom, else first empty slot. Null if full.
 */
export function findQuickTarget(
  c: Containers,
  container: ContainerId,
  itemId: ItemId,
): number | null {
  const slots = c[container];
  const max = maxStackOf(itemId);
  const mergeIdx = slots.findIndex((s) => s !== null && s.itemId === itemId && s.qty < max);
  if (mergeIdx !== -1) return mergeIdx;
  const emptyIdx = slots.findIndex((s) => s === null);
  return emptyIdx === -1 ? null : emptyIdx;
}

/**
 * Deposit `qty` of an item into a single container, merging into existing
 * stacks (respecting maxStack) before opening empty slots.
 * Returns the new slot array and how much could not be placed.
 */
export function depositIntoSlots(
  slots: ReadonlyArray<ItemStack | null>,
  itemId: ItemId,
  qty: number,
): { slots: ReadonlyArray<ItemStack | null>; leftover: number } {
  const max = maxStackOf(itemId);
  const next = [...slots];
  let remaining = qty;
  for (let i = 0; i < next.length && remaining > 0; i += 1) {
    const s = next[i];
    if (s && s.itemId === itemId && s.qty < max) {
      const add = Math.min(remaining, max - s.qty);
      next[i] = { itemId, qty: s.qty + add };
      remaining -= add;
    }
  }
  for (let i = 0; i < next.length && remaining > 0; i += 1) {
    if (next[i] === null) {
      const add = Math.min(remaining, max);
      next[i] = { itemId, qty: add };
      remaining -= add;
    }
  }
  return { slots: next, leftover: remaining };
}

/**
 * Award loot: bank first, inventory as overflow. Anything that fits nowhere
 * is not created (returned in `lost` so callers can decide to surface it).
 */
export function awardItem(
  c: Containers,
  itemId: ItemId,
  qty: number,
): { containers: Containers; lost: number } {
  const bankResult = depositIntoSlots(c.bank, itemId, qty);
  if (bankResult.leftover === 0) {
    return { containers: { ...c, bank: bankResult.slots }, lost: 0 };
  }
  const invResult = depositIntoSlots(c.inventory, itemId, bankResult.leftover);
  return {
    containers: { bank: bankResult.slots, inventory: invResult.slots },
    lost: invResult.leftover,
  };
}

/**
 * Place a stack into the inventory only (used by unequip/displacement):
 * merge under maxStack first, else first empty slot. Null when it cannot fit.
 */
export function placeInInventory(
  inventory: ReadonlyArray<ItemStack | null>,
  itemId: ItemId,
  qty: number,
): ReadonlyArray<ItemStack | null> | null {
  const { slots, leftover } = depositIntoSlots(inventory, itemId, qty);
  return leftover === 0 ? slots : null;
}

export type { EquipSlotId };
